import type { ReactNode } from "react";

/**
 * Schrittleiste „Hochladen · Vorprüfung · Lauf · Ergebnis“ (Idee 11, Design-Guide `dokument-ansicht.md`, Betreiber 22.10.2026 „Empfehlung
 * + 3, 5, 10, 12“): zeigt, wo ein Assistenten-Ablauf steht. Reine Anzeige, nicht bedienbar (keine Reiter, kein Springen): dafür gibt es
 * `PhasenLeiste`, die ausdrücklich NICHT für Assistenten-Schritte gedacht ist. Erledigte Schritte tragen einen Haken, der aktuelle die
 * Nummer im Ring (`aria-current="step"`), künftige eine gedämpfte Nummer. Der Zustand steht immer auch als Wort für Hilfstechnik
 * (`sr-only`), die Farbe trägt nie allein die Aussage.
 */
export interface Schritt {
  id: string;
  titel: string;
}

const KREIS_ERLEDIGT = "shrink-0 size-6 inline-flex justify-center items-center rounded-full text-xs font-medium bg-status-ok text-primary-foreground";
const KREIS_JETZT = "shrink-0 size-6 inline-flex justify-center items-center rounded-full text-xs font-medium bg-primary text-primary-foreground";
const KREIS_OFFEN = "shrink-0 size-6 inline-flex justify-center items-center rounded-full text-xs font-medium bg-muted text-muted-foreground-1";
const TITEL_JETZT = "text-sm font-medium text-foreground";
const TITEL_ANDERE = "text-sm text-muted-foreground-1";
const LINIE_ERLEDIGT = "hidden sm:block h-px w-8 bg-status-ok";
const LINIE_OFFEN = "hidden sm:block h-px w-8 bg-line-3";

export function SchrittLeiste({ schritte, aktuell, label }: { schritte: readonly Schritt[]; aktuell: number; label: string }): ReactNode {
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-actions-status-progress/ui-components-cat-progress-indicators/square-segmented-progress-meter */}
      <ol className="flex flex-wrap items-center gap-x-3 gap-y-2" aria-label={`${label}: Schritt ${aktuell} von ${schritte.length}`}>
        {schritte.map((s, i) => {
          const nr = i + 1;
          const erledigt = nr < aktuell;
          const jetzt = nr === aktuell;
          return (
            <li key={s.id} className="flex items-center gap-x-3" aria-current={jetzt ? "step" : undefined}>
              <span className="flex items-center gap-x-2">
                {erledigt ? (
                  <span className={KREIS_ERLEDIGT} aria-hidden="true">
                    <svg className="size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                ) : (
                  <span className={jetzt ? KREIS_JETZT : KREIS_OFFEN} aria-hidden="true">
                    {nr}
                  </span>
                )}
                <span className={jetzt ? TITEL_JETZT : TITEL_ANDERE}>
                  {s.titel}
                  <span className="sr-only">{erledigt ? " (erledigt)" : jetzt ? " (aktuell)" : " (folgt)"}</span>
                </span>
              </span>
              {nr < schritte.length && <span className={erledigt ? LINIE_ERLEDIGT : LINIE_OFFEN} aria-hidden="true" />}
            </li>
          );
        })}
      </ol>
    </>
  );
}
